import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import { Phone, Mail, MapPin, CheckCircle2, ArrowRight, Building2, Wrench, Key, Paintbrush, Clock, Shield, Users } from 'lucide-react';
import { companyInfo } from '../data/mock';

const LettingAgentMaintenance = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', properties: '', message: '', honeypot: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          properties_managed: `${formData.company} - ${formData.properties}`,
          job_description: `[Letting Agent Maintenance Enquiry] ${formData.message}`,
          trade_type: 'letting_agent',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/letting-agent-maintenance'
        }),
      });
      if (response.ok) { setIsSubmitted(true); setFormData({ name: '', email: '', phone: '', company: '', properties: '', message: '', honeypot: '' }); }
      else { setSubmitError(true); }
    } catch { setSubmitError(true); }
    finally { setIsSubmitting(false); }
  };

  const services = [
    { icon: Wrench, title: 'Tenant-Reported Maintenance', desc: 'Responding to maintenance issues reported by tenants — fast, professional resolution' },
    { icon: Clock, title: 'Reactive Repairs', desc: 'Urgent repairs when things break — damaged doors, failed locks, plumbing issues' },
    { icon: Key, title: 'End of Tenancy Works', desc: 'Preparing properties between tenants — repairs, decorating and general maintenance' },
    { icon: Building2, title: 'Void Turnarounds', desc: 'Complete void property works to get properties let-ready quickly' },
    { icon: Paintbrush, title: 'Decorating', desc: 'Property refreshes, periodic repainting, end of tenancy decorating' },
    { icon: Shield, title: 'Planned Maintenance', desc: 'Scheduled maintenance to prevent deterioration and costly emergency repairs' }
  ];

  const benefits = [
    'Single point of contact for multiple trades',
    'Multi-trade capability — carpentry, decorating, plastering, plumbing, electrical',
    'Understanding of letting industry requirements',
    'Fast turnaround to minimise void periods',
    'Clear communication and progress updates',
    'Photo documentation of completed works',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];

  const whyPartner = [
    { title: 'Reliability', desc: 'We turn up when we say, complete work to agreed timescales, and communicate clearly throughout' },
    { title: 'Multi-Trade Capability', desc: 'One contractor handles carpentry, decorating, plastering, plumbing, electrical — no need to coordinate multiple trades' },
    { title: 'Quality Standards', desc: 'Consistent quality across all work — important when you are responsible to landlord clients' },
    { title: 'Understanding', desc: 'We understand the letting industry — void periods cost money, tenant satisfaction matters, landlords expect updates' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Property Maintenance for Letting Agents | Contractor Support | Expert Property Maintenance</title>
        <meta name="description" content="Property maintenance services for letting agents in Manchester. Tenant-reported repairs, void turnarounds, planned maintenance. One contractor, all trades. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/letting-agent-maintenance" />
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              For Letting Agents
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Property Maintenance for Letting Agents
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Reliable property maintenance support for letting agencies across Greater Manchester. 
              One contact for all your managed property repairs, maintenance and refurbishment needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">Discuss Your Requirements<ArrowRight className="w-5 h-5 ml-2" /></a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}><Phone className="w-5 h-5 mr-2" />{companyInfo.phone}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Letting Agents Need Good Contractors */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Why Letting Agents Need Reliable Contractors
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                As a letting agent, you're in the middle — landlords expect their properties maintained 
                properly, tenants expect issues resolved promptly. When contractors let you down, you 
                take the complaints.
              </p>
              <p className="text-brand-slate mb-6">
                Finding reliable maintenance contractors is challenging. You need people who turn up, 
                complete work to a good standard, communicate clearly, and don't leave you chasing for 
                updates. Ideally, you want fewer contractors to manage — one contact who can handle 
                multiple trades.
              </p>
              <p className="text-brand-slate mb-8">
                Expert Property Maintenance provides multi-trade property maintenance support for letting 
                agencies. One phone number for carpentry, decorating, plastering, plumbing and electrical — 
                reducing your coordination overhead and giving you consistent quality across different trades.
              </p>
              <div className="space-y-3">
                {benefits.slice(0, 6).map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-brand-grey rounded-2xl p-8">
              <h3 className="text-xl font-bold text-brand-navy mb-6">Services for Letting Agents</h3>
              <div className="space-y-4">
                {services.map((service) => (
                  <div key={service.title} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-10 h-10 bg-brand-grey rounded-lg flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-5 h-5 text-brand-yellow" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-brand-navy">{service.title}</h4>
                      <p className="text-sm text-brand-slate">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">
            Why Work With Expert Property Maintenance
          </h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            What letting agents should expect from a property maintenance contractor.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {whyPartner.map((item) => (
              <Card key={item.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{item.title}</h3>
                  <p className="text-brand-slate">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Proposition */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-navy rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Users className="w-12 h-12 text-brand-yellow mb-6" />
                <h2 className="text-3xl font-bold text-white mb-4">
                  One Contact Instead of Many
                </h2>
                <p className="text-slate-300 mb-6">
                  The traditional approach to property maintenance means coordinating multiple contractors — 
                  a painter for decorating, a joiner for the door repairs, a plasterer for the walls, a 
                  plumber for the leaky tap. Each needs briefing, scheduling and chasing.
                </p>
                <p className="text-slate-400 mb-6">
                  Expert Property Maintenance handles multiple trades through one contact. You tell us 
                  what needs doing, we complete the work — whether that's one trade or several.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow" /> Carpentry
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow" /> Decorating
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow" /> Plastering
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow" /> Plumbing
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow" /> Electrical
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-yellow" /> Handyman
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Reactive Maintenance</h4>
                  <p className="text-sm text-slate-400">Tenant-reported issues handled promptly</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Void Turnarounds</h4>
                  <p className="text-sm text-slate-400">Complete works to minimise void periods</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Planned Maintenance</h4>
                  <p className="text-sm text-slate-400">Scheduled works across your portfolio</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Refurbishment</h4>
                  <p className="text-sm text-slate-400">Larger renovation projects</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Service Pages</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/reactive-property-maintenance" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Reactive Maintenance</h4>
              <p className="text-sm text-brand-slate">Urgent repair response</p>
            </a>
            <a href="/void-property-works" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Void Property Works</h4>
              <p className="text-sm text-brand-slate">Complete turnarounds</p>
            </a>
            <a href="/end-of-tenancy" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">End of Tenancy</h4>
              <p className="text-sm text-brand-slate">Repairs between tenancies</p>
            </a>
            <a href="/property-refurbishment" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Property Refurbishment</h4>
              <p className="text-sm text-brand-slate">Major renovations</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Discuss Your Agency's Needs</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about your agency and the properties you manage. We will discuss how we can support 
                your maintenance requirements.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><Phone className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">{companyInfo.phone}</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><Mail className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow">{companyInfo.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><MapPin className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-slate-800 rounded-xl">
                <div className="text-sm text-slate-400 mb-2">Insurance & Guarantee</div>
                <div className="text-white font-medium">{companyInfo.insurance.publicLiability}</div>
                <div className="text-white font-medium">{companyInfo.guarantee}</div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch to discuss your requirements.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Get in Touch</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" /></div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Agency Name</label><Input value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} placeholder="ABC Lettings" className="h-12" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@abclettings.com" className="h-12" /></div>
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label><Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Properties Managed (approx)</label><Input value={formData.properties} onChange={(e) => setFormData({...formData, properties: e.target.value})} placeholder="e.g., 50+ in South Manchester" className="h-12" /></div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">What maintenance support do you need? *</label><Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us about your current maintenance challenges and what you're looking for..." rows={4} /></div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send Enquiry'}</Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default LettingAgentMaintenance;
